import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, FlaskConical, Loader2, Plus } from "lucide-react";
import { api } from "@/api/client";
import { downloadBlob } from "@/lib/download";
import type { ScheduleWithStudy, Study } from "../../../types";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { CreateStudyForm } from "./CreateStudyForm";
import { StudyDetailView } from "./StudyDetailView";
import { StudyCard } from "./StudyCard";

const studiesCsvFilename = "studies.csv";

/**
 * Gets a safe display message for an unknown caught error.
 *
 * @param error Caught error value.
 * @param fallback Fallback message.
 * @return Displayable error message.
 */
function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;

  return fallback;
}

/**
 * Renders the studies list or selected study detail.
 *
 * @return Studies view.
 */
export function StudiesView() {
  const navigate = useNavigate();
  const { studyId } = useParams<{ studyId: string }>();
  const [studies, setStudies] = useState<Study[]>([]);
  const [schedules, setSchedules] = useState<ScheduleWithStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const fetchStudies = async () => {
    setLoading(true);
    try {
      const data = await api.studies.list();
      setStudies(data);
      setError(null);
      // Schedule badges are decoration — don't fail the list if this errors.
      api.schedules.list().then(setSchedules).catch(() => {});
    } catch (err: any) {
      setError(err?.message || "Failed to load studies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStudies(); }, []);

  /**
   * Exports the active workspace's study list as CSV.
   *
   * @return Promise that resolves after the download is triggered.
   */
  const handleExportCsv = async (): Promise<void> => {
    setIsExporting(true);
    setExportError("");

    try {
      const csvBlob = await api.studies.exportListCsv();
      downloadBlob(csvBlob, studiesCsvFilename);
    } catch (exportFailure) {
      setExportError(getErrorMessage(exportFailure, "Failed to export studies"));
    } finally {
      setIsExporting(false);
    }
  };

  // If a study route is selected, show its detail view.
  if (studyId) {
    const selectedStudyId = Number(studyId);

    if (!Number.isInteger(selectedStudyId) || selectedStudyId < 1) {
      return <p className="text-sm text-slate-500">Study not found.</p>;
    }

    return (
      <StudyDetailView
        studyId={selectedStudyId}
        onBack={() => { navigate("/app/studies"); fetchStudies(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleExportCsv}
            disabled={isExporting || loading}
            aria-busy={isExporting}
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-1.5" />
            New Study
          </Button>
        </div>
        {exportError && (
          <p className="text-right text-[13px] text-red-600" role="alert">
            {exportError}
          </p>
        )}
      </div>

      {showForm && (
        <div className="animate-fade-up">
          <CreateStudyForm onSuccess={() => { setShowForm(false); fetchStudies(); }} />
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-100 rounded-lg animate-subtle-pulse" />
          ))}
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={fetchStudies} />
      ) : studies.length === 0 ? (
        <EmptyState
          icon={FlaskConical}
          title="No studies yet"
          description="Create a study to start tracking brand perception over time."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {studies.map(study => (
            <StudyCard
              key={study.id}
              study={study}
              schedule={schedules.find(s => s.study_id === study.id)}
              onClick={() => navigate(`/app/studies/${study.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
